import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
    Container, Box, Typography, TextField, Button, Alert, Paper, 
    InputAdornment, IconButton
} from '@mui/material';
import { Lock, Visibility, VisibilityOff, Key } from '@mui/icons-material';
import api from '../../api/axios';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError("Passwords don't match");
            return;
        }
        
        setLoading(true);
        setError('');
        try {
            await api.post('/auth/reset-password', { token, new_password: password });
            setSuccess(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to reset password. Link may be expired.');
        }
        setLoading(false);
    };

    if (!token) {
        return (
            <Container maxWidth="xs" sx={{ mt: 8 }}>
                <Alert severity="error">Invalid reset link.</Alert>
            </Container>
        );
    }

    if (success) {
        return (
            <Container component="main" maxWidth="xs" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
                <Paper elevation={0} sx={{ p: 4, width: '100%', textAlign: 'center', border: '1px solid #E2E8F0', borderRadius: 3 }}>
                    <Typography variant="h5" gutterBottom color="success.main" fontWeight={700}>
                        Password Reset!
                    </Typography>
                    <Typography paragraph>
                        Your password has been updated successfully.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Redirecting to login...
                    </Typography>
                </Paper>
            </Container>
        );
    }

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            bgcolor: '#F8FAFC',
            backgroundImage: 'radial-gradient(#E2E8F0 1px, transparent 1px)',
            backgroundSize: '20px 20px'
        }}>
            <Container component="main" maxWidth="xs">
                <Paper 
                    elevation={0} 
                    sx={{ 
                        p: 4, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        border: '1px solid #E2E8F0',
                        borderRadius: 3,
                        bgcolor: 'white',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ 
                            width: 56, height: 56, bgcolor: 'primary.light', borderRadius: '16px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
                            color: 'primary.main'
                        }}>
                            <Key fontSize="large" />
                        </Box>
                        <Typography component="h1" variant="h5" fontWeight={800} color="text.primary">
                            Set New Password
                        </Typography>
                    </Box>

                    {error && <Alert severity="error" sx={{ width: '100%', mb: 3 }}>{error}</Alert>}

                    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="New Password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock color="action" fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: 2 }
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="confirmPassword"
                            label="Confirm Password"
                            type={showPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock color="action" fontSize="small" />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: 2 }
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            sx={{ mt: 3, mb: 2, py: 1.5, fontWeight: 700, borderRadius: 2 }}
                            disabled={loading}
                        >
                            {loading ? 'Updating...' : 'Update Password'}
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default ResetPassword;
